import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export type ViewMode = 'pipeline' | 'table' | 'map' | 'gallery';

const STORAGE_KEY = 'home:viewMode';
const VALID_MODES: ViewMode[] = ['pipeline', 'table', 'map', 'gallery'];

const isValidMode = (value: string | null): value is ViewMode =>
  !!value && (VALID_MODES as string[]).includes(value);

const isEnabled = (mode: ViewMode, enabled?: ViewMode[]): boolean =>
  !enabled || enabled.includes(mode);

const readInitialMode = (
  urlValue: string | null,
  enabledModes?: ViewMode[]
): ViewMode => {
  if (isValidMode(urlValue) && isEnabled(urlValue, enabledModes))
    return urlValue;
  if (typeof window !== 'undefined') {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isValidMode(stored) && isEnabled(stored, enabledModes)) return stored;
  }
  return 'pipeline';
};

export interface UseViewModeOptions {
  enabledModes?: ViewMode[];
}

export interface UseViewModeReturn {
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
}

export function useViewMode(options?: UseViewModeOptions): UseViewModeReturn {
  const { enabledModes } = options ?? {};
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewModeState] = useState<ViewMode>(() =>
    readInitialMode(searchParams.get('view'), enabledModes)
  );

  useEffect(() => {
    const fromUrl = searchParams.get('view');
    if (
      isValidMode(fromUrl) &&
      isEnabled(fromUrl, enabledModes) &&
      fromUrl !== viewMode
    ) {
      setViewModeState(fromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, viewMode]);

  useEffect(() => {
    if (enabledModes && !isEnabled(viewMode, enabledModes)) {
      setViewModeState('pipeline');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabledModes?.join(',')]);

  const setViewMode = useCallback(
    (mode: ViewMode) => {
      if (!isEnabled(mode, enabledModes)) return;
      setViewModeState(mode);
      window.localStorage.setItem(STORAGE_KEY, mode);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('view', mode);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams, enabledModes]
  );

  return { viewMode, setViewMode };
}
